import { CalendarIcon } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';

import { CalendarPanel } from './calendar-panel';
import { PickerFooter } from './picker-footer';
import { dateToValues, valuesToDate } from './segments';
import { isDateInRange, nowIn, parseDate, type CalendarDate } from './temporal';
import { TemporalField, type TemporalInputProps, type TemporalPanelApi } from './temporal-field';

export type DatePickerProps = TemporalInputProps;

/**
 * A themed `<input type="date">`: `value` is `YYYY-MM-DD` (or `''`), `min`/`max` too. Drop it in
 * where `<Input type="date" {...field} />` was.
 */
export function DatePicker(props: DatePickerProps) {
  const { t } = useTranslation('common');
  const min = parseDate(props.min);
  const max = parseDate(props.max);
  return (
    <TemporalField
      {...props}
      kind="date"
      minuteStep={1}
      triggerIcon={<CalendarIcon aria-hidden />}
      triggerLabel={t('dateTime.chooseDate')}
      renderPanel={(api) => <DatePanel api={api} min={min} max={max} />}
    />
  );
}

function DatePanel({
  api,
  min,
  max,
}: {
  api: TemporalPanelApi;
  min: CalendarDate | null;
  max: CalendarDate | null;
}) {
  const { t } = useTranslation('common');
  const today = nowIn(api.timeZone).date;
  const choose = (date: CalendarDate, close: boolean) => {
    api.update(dateToValues(date));
    if (close) api.close();
  };
  return (
    <div>
      <CalendarPanel
        locale={api.locale}
        selected={valuesToDate(api.segments)}
        today={today}
        min={min}
        max={max}
        focusRef={api.initialFocusRef}
        escapeRef={api.escapeRef}
        onSelect={(date, intent) => choose(date, intent !== 'move')}
      />
      <PickerFooter
        onClear={() => {
          api.update({});
          api.close();
        }}
        nowLabel={t('dateTime.today')}
        nowDisabled={!isDateInRange(today, min, max)}
        onNow={() => choose(today, true)}
      />
    </div>
  );
}
